import Axios from 'axios';

// reducers & actions
import { setLoadingStatus, setSavingStatus } from '../../../reducers/notification';
import {
  setBankAccountsList,
  setBankAccountsActivityLog,
  setBankAccountFormData,
  setBankAccountFormStores,
} from '../../../reducers/bankAccounts';
import { notificationSuccess, notificationError } from '../../../core/actions';

// local
import { getSearchParams } from '../Reports/BankTransaction/utils';
import { PAYMENTS_API_URL } from '../../../constants';

export const getBluesnapToken = merchantId =>
  new Promise((resolve, reject) => {
    if (!merchantId) {
      return null;
    }

    return Axios.get(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/getPaymentFieldsToken`)
      .then(({ data }) => {
        return resolve(data.pfToken);
      })
      .catch(err => {
        console.log({ err });
        notificationError('Please try again');
        return reject(err);
      });
  });

export const getBankAccountsList = merchantId =>
  new Promise((resolve, reject) => {
    setLoadingStatus(true);

    return Axios.get(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/list`)
      .then(({ data }) => {
        const filterDeletedAccounts = data.filter(({ status }) => status !== 'DELETED'); // Bank Accounts page doesnt support DELETED - breaks page
        setBankAccountsList(filterDeletedAccounts);
        return filterDeletedAccounts;
      })
      .catch(err => {
        console.log({ err });
        notificationError('Bank accounts did not load');
        reject(err);
      })
      .finally(() => {
        setLoadingStatus(false);
      });
  });

export const getBankAccountFormData = merchantId =>
  new Promise((resolve, reject) => {
    setLoadingStatus(true);

    return Axios.get(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/getPaymentFields`)
      .then(({ data }) => {
        setBankAccountFormData(data.businessAndReps[0]);
        setBankAccountFormStores(data.unassignedStores);
        resolve();
      })
      .catch(err => {
        console.log({ err });
        notificationError('Bank account form data did not load');
        reject();
      })
      .finally(() => {
        setLoadingStatus(false);
      });
  });
export const getBankAccountStores = async (merchantId, accountId) => {
  setLoadingStatus(true);

  try {
    const { data } = await Axios.get(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/stores/${accountId}`);

    return data;
  } catch (error) {
    notificationError('Bank account stores did not load');
  } finally {
    setLoadingStatus(false);
  }
};
export const getStoresBankAccount = async (merchantId, accountId) => {
  setLoadingStatus(true);

  try {
    const { data } = await Axios.get(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/stores/${accountId}`);

    return data;
  } catch (error) {
    notificationError('Bank account stores did not load');
  } finally {
    setLoadingStatus(false);
  }
};

export const updateBankAccountStores = async (merchantId, accountId, storeIDs) => {
  setLoadingStatus(true);

  try {
    const { data } = await Axios.put(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/stores/${accountId}`, { storeIDs });

    return data;
  } catch (error) {
    notificationError('Bank account stores did not load');
  } finally {
    setLoadingStatus(false);
  }
};

export const getBankAuditLog = (merchantId, { ascending }) =>
  new Promise((resolve, reject) => {
    setLoadingStatus(true);
    const searchParams = getSearchParams({
      ascending,
    });
    return Axios.get(`${PAYMENTS_API_URL}/v1/reports/bankAuditLog/${merchantId}${searchParams}`)
      .then(({ data }) => {
        setBankAccountsActivityLog(data);
        resolve();
      })
      .catch(err => {
        console.log({ err });
        notificationError('Bank audit log did not load');
        reject();
      })
      .finally(() => {
        setLoadingStatus(false);
      });
  });

export const deleteBankAccount = async (merchantId, id) => {
  setSavingStatus(true);

  try {
    const data = await Axios.put(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/delete/${id}`);

    notificationSuccess('Your account was archived');

    return data;
  } catch (error) {
    notificationError('Account was not deleted');

    return error;
  } finally {
    setSavingStatus(false);
  }
};

export const setPrimaryAccountWithoutEffects = async (merchantId, id) => {
  setSavingStatus(true);

  try {
    const data = await Axios.put(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/setPrimary/${id}`);

    return data;
  } catch (error) {
    notificationError('Primary account has not been updated');
  } finally {
    setSavingStatus(false);
  }
};

export const setPrimaryAccount = (merchantId, id, update = true) =>
  new Promise((resolve, reject) => {
    setSavingStatus(true);

    return Axios.put(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/setPrimary/${id}`)
      .then(() => {
        setSavingStatus(false);

        if (update) {
          return getBankAccountsList(merchantId).then(() => {
            notificationSuccess('Your primary account has been updated');
            resolve();
          });
        }
      })
      .catch(err => {
        console.log({ err });
        setSavingStatus(false);
        notificationError('Primary account has not been updated');
        reject();
      });
  });

export const getPaymentSettings = merchantId =>
  new Promise((resolve, reject) => {
    setLoadingStatus(true);

    if (!merchantId) {
      return null;
    }

    return Axios.get(`${PAYMENTS_API_URL}/v1/paymentSettings/${merchantId}`)
      .then(({ data }) => {
        const settings = {
          displayName: data.paymentSettings.businessName,
          requestMessage: data.paymentSettings.paymentRequestDefaultMessage,
          reminderMessage: data.paymentSettings.paymentReminderDefaultMessage,
          confirmationMessage: data.paymentSettings.paymentConfirmationDefaultMessage,
        };
        setLoadingStatus(false);
        resolve(settings);
      })
      .catch(err => {
        console.log({ err });
        setLoadingStatus(false);
        notificationError('Your settings did not load');
        reject(err);
      });
  });

export const savePaymentSettings = (
  merchantId,
  { displayName, requestMessage, reminderMessage, confirmationMessage },
) =>
  new Promise((resolve, reject) => {
    setSavingStatus(true);

    return Axios.post(`${PAYMENTS_API_URL}/v1/paymentSettings/${merchantId}`, {
      businessName: displayName,
      paymentRequestDefaultMessage: requestMessage,
      paymentReminderDefaultMessage: reminderMessage,
      paymentConfirmationDefaultMessage: confirmationMessage,
    })
      .then(() => {
        setSavingStatus(false);
        notificationSuccess('Your settings have saved');
        getPaymentSettings(merchantId);
      })
      .catch(err => {
        console.log({ err });
        setSavingStatus(false);
        notificationError('Your settings did not save');
      });
  });

/**
 * Sola merchant boarding.
 *
 * A different endpoint from addBankAccount, which is BlueSnap's: that one is called AFTER
 * BlueSnap's SDK has captured the bank details in the browser and carries a pfToken. Sola
 * has no such SDK for boarding -- the whole application, banking included, is submitted
 * server-side, so it is posted here in one request.
 *
 * The payload carries routing/account numbers and SSNs. They are forwarded to Sola and are
 * never persisted by our API. Do not log `data`.
 */
export const addSolaBankAccount = (merchantId, data) =>
  new Promise((resolve, reject) => {
    Axios.post(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/sola/bankAccount`, JSON.stringify(data), {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then(({ data: response }) => {
        // Boarding can outlive the request: the API answers SUBMIT_PENDING rather than making
        // the merchant wait, and records the outcome when the processor finally responds. The
        // account is real either way -- saying "added" would be wrong only in that it implies
        // it is ready to take payments, which it is not until review completes.
        if (response?.data?.status === 'SUBMIT_PENDING') {
          notificationSuccess('Bank account submitted. It is being reviewed and will show as Active once approved.');
        } else {
          notificationSuccess('Bank account was added');
        }

        return resolve(response);
      })
      .catch(({ response }) => {
        // The Sola route answers with the field-level problems its validation found, so they
        // can be surfaced rather than a generic failure.
        const errors = response?.data?.errorData || response?.data?.error || [];
        notificationError('Bank account was not added');
        reject(Array.isArray(errors) ? errors : [errors]);
      });
  });

export const addBankAccount = (merchantId, data) =>
  new Promise((resolve, reject) => {
    return Axios.post(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}`, JSON.stringify(data), {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
      .then(({ data }) => {
        if (data === 'ok') {
          notificationSuccess('Bank account was added');
        }
        if (data === 'still-pending') {
          notificationSuccess('We are still creating your account. Please check back in a few minutes.');
        }
        return resolve(data);
      })
      .catch(({ response }) => {
        const errors = response?.data?.errorData || [];
        notificationError('Bank account was not added');
        reject(errors);
      });
  });

/**
 * Which processor this merchant's NEXT bank account will be created on, as the payments backend
 * decides it: its merchant_config PAYMENT_PROVIDER row, or the environment's default
 * (DEFAULT_PAYMENT_PROVIDER, SOLA when unset). Asked rather than inferred, so the Dashboard can
 * never apply a different default from the backend's.
 */
export const getPaymentProvider = async merchantId => {
  const { data } = await Axios.get(`${PAYMENTS_API_URL}/v1/merchant/${merchantId}/paymentProvider`);

  return data.provider;
};
